/* eslint-disable unicorn/filename-case -- TODO rename later */
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingList,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListItem,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import {
  createContext,
  ElementType,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { MoreVertical } from "../../../assets/svg";
import { ListItem } from "../../../types/option";

interface SelectContextValue {
  selectedIndex: number | null | undefined;
  getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
  handleSelect: (menuItemIndex: number | null) => void;
}

const SelectContext = createContext<SelectContextValue>({} as SelectContextValue);

function Select({
  ariaLabel,
  children,
  triggerLabelIcon,
  triggerVariant,
  dataTestId,
  placement,
}: {
  ariaLabel: string;
  children: ReactNode;
  triggerLabelIcon: ReactElement;
  triggerVariant: "solid" | "transparent";
  dataTestId?: string;
  placement?: Placement;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement || "bottom-end",
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    // middleware: [autoPlacement()],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const handleSelect = useCallback((index: number | null) => {
    setSelectedIndex(index);
    setIsOpen(false);
  }, []);

  const listNav = useListNavigation(context, {
    loop: true,
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, click, dismiss, role]);

  const selectContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  return (
    <>
      {/* Make a TriggerButton component that the consumer is expected to use to wrap the trigger label icon in order to create an opt-in, composable styled trigger button */}
      <div
        aria-label={ariaLabel}
        data-testid={dataTestId}
        role="button"
        // TODO: Remove the mx-2.5 and adjust consumer code to account for the new lack of margin.
        className={`mx-2.5 flex min-w-4 cursor-pointer items-center justify-center p-0 text-base font-medium text-gray-700 focus-visible:p-0 ${triggerVariant === "solid" ? "bg-gray-100" : "bg-transparent"} rounded-md`}
        ref={refs.setReference}
        tabIndex={0}
        {...getReferenceProps()}
      >
        {triggerLabelIcon}
      </div>
      <SelectContext.Provider value={selectContext}>
        {isOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className="z-[100] mt-2 min-w-[150px] rounded-md border border-gray-200 bg-white shadow-xs"
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                {children}
              </FloatingList>
            </div>
          </FloatingFocusManager>
        )}
      </SelectContext.Provider>
    </>
  );
}

function Option({
  icon,
  label,
  value,
  selectedIndex,
  onItemClick,
  dataTestId,
}: {
  icon: ElementType | ReactNode;
  label: string;
  value: string;
  selectedIndex: number | null;
  onItemClick: (itemValue: string) => void;
  dataTestId?: string;
}) {
  const { getItemProps, handleSelect } = useContext(SelectContext);

  const { index, ref } = useListItem({ label });

  const isSelected = selectedIndex === index;

  return (
    <button
      className={`flex w-full cursor-pointer items-center gap-1 border-0 px-3 py-2 text-left text-sm font-medium text-gray-600 first:rounded-t-md last:rounded-b-md enabled:hover:bg-gray-50 enabled:hover:text-gray-700 ${isSelected ? "bg-gray-100" : "bg-white"}`}
      ref={ref}
      role="option"
      type="button"
      aria-selected={isSelected}
      data-testid={dataTestId} // Add data-testid here
      {...getItemProps({
        onClick: () => {
          onItemClick(value);
          handleSelect(index);
        },
      })}
    >
      {icon && (icon as ReactNode)}
      {label}
    </button>
  );
}

/**
 * @deprecated Use `~/components/ui/dropdown-menu` instead.
 *
 * @example
 * import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "~/components/ui/dropdown-menu"
 */
export default function Menu({
  ariaLabel,
  dataTestId,
  options,
  selectedItemIndex,
  placement,
  triggerLabelIcon = <MoreVertical width="18px" height="18px" />,
  triggerVariant = "transparent",
  onMenuItemClick,
}: {
  ariaLabel: string;
  dataTestId?: string;
  options: ListItem[];
  selectedItemIndex?: number | null | undefined;
  placement?: Placement;
  triggerLabelIcon?: ReactElement;
  triggerVariant?: "solid" | "transparent";
  onMenuItemClick: (itemValue: string) => void;
}) {
  return (
    <Select
      ariaLabel={ariaLabel}
      dataTestId={dataTestId}
      placement={placement}
      triggerLabelIcon={triggerLabelIcon}
      triggerVariant={triggerVariant}
    >
      {options.map((option) => (
        <Option
          key={option.value}
          icon={option.icon}
          label={option.label}
          value={option.value}
          selectedIndex={selectedItemIndex!}
          onItemClick={onMenuItemClick}
          dataTestId={option.dataTestId} // Pass dataTestId from option to Option component
        />
      ))}
    </Select>
  );
}
